// pages/MediaManagementPage.tsx
import { axiosInstance } from "@/api/api-client";
import { MediaGridPane } from "@/components/media/MediaGridPane";
import { MediaSearchBar } from "@/components/media/MediaSearchBar";
import { MediaSidebarPanel } from "@/components/media/MediaSidebarPanel";
import { createNewFolder, deleteFolder, deleteMedia } from "@/hooks/media-api";
import { useMediaLibrary } from "@/hooks/use-media-library";
import { MediaAsset } from "@/types/media";
import { useEffect } from "react";
import FolderToolbar from "@/components/media/FolderToolbar";
import MyContainer from "@/components/MyContainer";
import { toast } from "@/hooks/use-toast";

export default function MediaManagementPage() {
  const state = useMediaLibrary(null);
  const {
    fetchFolderTree,
    fetchMediaAssets,
    currentFolder,
    setCurrentFolder,
    currentParentId,
    setCurrentParentId,
    query
  } = state;

  useEffect(() => {
    fetchFolderTree(currentFolder);
    fetchMediaAssets({ reset: true, parentId: currentFolder?.id || null });
  }, [currentFolder]);

  // --- Handlers ---
  const onCreateFolder = async (name: string) => {
    const parentId = state.currentFolder ? state.currentFolder.id : null;
    const path = `${state.currentFolder?.fileUrl || ""}/${name}`;
    await createNewFolder(parentId, name, path);
    await state.fetchFolderTree(state.currentFolder);
    await state.fetchMediaAssets({ reset: true, parentId: currentFolder?.id || null });
  };

  const onCopyUrl = async (item: MediaAsset) => {
    const url = (item as any).fileUrl || (item as any).s3Url || "";
    if (!url) return;
    await navigator.clipboard.writeText(url);
    toast({ title: "Copied", description: "URL copied to clipboard" });
  };

  const onDelete = async (item: MediaAsset) => {
    const resp = await deleteMedia(item.id);
    if (resp.success) {
      toast({ title: "Deleted", description: resp.message || `"${item.fileName}" deleted successfully.` });
      state.resetList(currentFolder?.id || null);
      await state.fetchFolderTree(state.currentFolder);
    } else {
      toast({ title: "Error deleting media", description: resp.message || "Cannot delete media", variant: "destructive" });
    }
  };

  const onUploadComplete = async () => {
    await state.fetchMediaAssets({ reset: true, parentId: currentFolder?.id || null });
    await state.fetchFolderTree(state.currentFolder);
  };

  const handdleDleeteFolder = async () => {
    if (currentParentId) {
      const resp = await deleteFolder(currentParentId);
      if (resp.success) {
        setCurrentParentId(null);
        setCurrentFolder(null); // Clear selection after delete
        await fetchFolderTree(null);
        await fetchMediaAssets({ reset: true, parentId: null, query: query });
      } else {
        toast({ title: "Error", description: resp.message, variant: "destructive" });
      }
    }
  };

  const handleSearch = async () => {
    await state.fetchMediaAssets({
      reset: true,
      parentId: currentFolder?.id || null,
      query: state.query
    });
  };

  return (
    <MyContainer
      title="Media Management"
      enableBack={false}
      subtitle="Manage your media"
      disableContentPadding={true}
    >
      {/* Main Layout Container:
          - Mobile: flex-col (Sidebar trigger top, then content)
          - Desktop: flex-row (Sidebar left, content right)
      */}
      <div className="flex flex-col md:flex-row h-[calc(100vh-theme(spacing.16))] md:h-screen overflow-hidden">

        {/* SIDEBAR: Becomes a top-bar trigger on mobile, fixed sidebar on desktop */}
        <MediaSidebarPanel state={state} onCreateFolder={onCreateFolder} />

        {/* CONTENT AREA */}
        <div className="flex-1 flex flex-col min-w-0 bg-white overflow-hidden">

          {/* TOOLBAR: Breadcrumbs and Action buttons */}
          <FolderToolbar
            currentPath={currentFolder?.fileName}
            onCreateFolder={onCreateFolder}
            onDeleteFolder={handdleDleeteFolder}
            onNavigate={(path) => {
              // If path is empty, go to root
              if (!path) {
                setCurrentFolder(null);
                setCurrentParentId(null);
              }
            }}
            disableDelete={!currentFolder}
            mediaState={state}
            onUploadComplete={onUploadComplete}
          />

          {/* SEARCH BAR */}
          <div className="px-4 py-2 border-b bg-slate-50/50">
            <MediaSearchBar state={state} onSearch={handleSearch} />
          </div>

          {/* GRID: Must be flex-1 with min-h-0 to enable internal scrolling */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            <MediaGridPane
              state={state}
              multi
              onCopyUrl={onCopyUrl}
              onDelete={onDelete}
            />
          </div>
        </div>
      </div>
    </MyContainer>
  );
}
